import { Box, Drawer, Typography, useMediaQuery, Backdrop } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cIconSvg from "../../asset/images/c-icon.svg";
import { PTTypography } from "../components/ui";
import { useLanguage, useLoadUser } from "../hooks";
import FirstHeader from "./components/FirstHeader";
import LeftBar from "./components/LeftBar";
import SecondHeader from "./components/SecondHeader";
import GlobalPaymentDialog from "./components/PaymentDialog";

function SecureNavBar({ children, ...props }) {
  const { data: user, isFetching } = useLoadUser();
  const [openPaymentDialog, setOpenPaymentDialog] = React.useState(false);
  const [needsPayment, setNeedsPayment] = React.useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLang } = useLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMidium = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [isLeftBarOpen, setIsLeftBarOpen] = React.useState(true);
  const isArabic = currentLang === "ar";

  // Vérifier si les données de paiement sont disponibles après le chargement de l'utilisateur
  React.useEffect(() => {
    if (user && user.cmiPaymentRequest && !location.pathname.includes('/payment/')) {
      setNeedsPayment(true);
      setOpenPaymentDialog(true);
    } else {
      setNeedsPayment(false);
    }
  }, [user, location.pathname]);

  React.useEffect(() => {
    if (isMobile || isMidium) setIsLeftBarOpen(false);
  }, [isMobile, isMidium]);

  const toggleLeftBar = () => {
    setIsLeftBarOpen(!isLeftBarOpen);
  };

  // Le dialog ne peut pas être fermé si paiement est requis
  const handleClosePaymentDialog = () => {
    if (!needsPayment) {
      setOpenPaymentDialog(false);
    }
  };

  const handleClick = (r) => {
    const urlParams = new URLSearchParams(location.search);
    const lang = urlParams.get("lang");
    const newPath = `${r.path}?lang=${lang}`;
    navigate(newPath);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* Backdrop pour bloquer l'interface quand le paiement est requis */}
      <Backdrop
        sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 1 }}
        open={needsPayment && openPaymentDialog}
      />

      {/* Popup de paiement global */}
      {user && user.cmiPaymentRequest && (
        <GlobalPaymentDialog
          open={openPaymentDialog}
          onClose={handleClosePaymentDialog}
          cmiPaymentRequest={user.cmiPaymentRequest}
          disableEscapeKeyDown={needsPayment}
          disableBackdropClick={needsPayment}
        />
      )}

      {/* Mobile Drawer */}
      <Drawer
        anchor={isArabic ? "right" : "left"}
        open={isMobile && isLeftBarOpen}
        onClose={toggleLeftBar}
        sx={{
          "& .MuiDrawer-paper": {
            width: 250,
            boxSizing: "border-box",
          },
        }}
      >
        <LeftBar
          user={user}
          isMidium={isMidium}
          isMobile={isMobile}
          handleClick={handleClick}
        />
      </Drawer>

      {/* Desktop LeftBar (fixed) */}
      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            [isArabic ? "right" : "left"]: 0,
            top: 28,
            bottom: 0,
            //width: 250,
            zIndex: theme.zIndex.drawer,
            display: isLeftBarOpen ? "block" : "none",
          }}
        >
          <LeftBar
            user={user}
            isMidium={isMidium}
            isMobile={isMobile}
            handleClick={handleClick}
            toggleLeftBar={toggleLeftBar}
          />
        </Box>
      )}

      {/* Fixed Headers */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 28,
          backgroundColor: "#F4F6F8",
          width: "100%",
          zIndex: theme.zIndex.appBar,
        }}
      ></Box>
      <Box
        sx={{
          position: "fixed",
          top: 28,
          [isArabic ? "right" : "left"]: isLeftBarOpen && !isMobile ? "calc(19vw + 22px)" : "5px",
          [isArabic ? "left" : "right"]: 20,
          zIndex: theme.zIndex.appBar,
          backgroundColor: "#F4F6F8",
        }}
      >
        <FirstHeader toggleLeftBar={toggleLeftBar} isMobile={isMobile} />
        <SecondHeader
          user={user}
          isMobile={isMobile}
          handleClick={handleClick}
        />
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flexGrow: 1,
          marginTop: "140px", // Adjust based on header height
          [isArabic ? "marginRight" : "marginLeft"]: isLeftBarOpen && !isMobile ? "calc(19vw + 12px)" : 0,
          overflowY: "auto",
          pb: "40px",
          pl: isMobile ? "4px" : "20px",
          pr: isMobile ? "4px" : "20px",
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          // Désactiver l'interaction avec le contenu quand le paiement est requis
          pointerEvents: needsPayment ? "none" : "auto",
          filter: needsPayment ? "blur(3px)" : "none",
        }}
      >
        {children}
      </Box>

      {/* Fixed Footer */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          textAlign: "center",
          bottom: "0px",
          margin: "0 auto",
          height: "28px",
          backgroundColor: "#FFFFFF",
          zIndex: theme.zIndex.appBar,
          width: "auto",
          pt: "5px",
        }}
      >
        <PTTypography variant="body3" sx={{ color: "#000000", mr: "10px" }}>
          {new Date().getFullYear()}
        </PTTypography>
        <img src={cIconSvg} alt="Logo" style={{}} />
        <Typography variant="body3" sx={{ color: "#000000", ml: "10px" }}>
          Portnet .SA
        </Typography>
      </Box>
    </Box>
  );
}

export default SecureNavBar;
